// SPDX-License-Identifier: GPL-2.0-or-later
/**
 * The design kit chosen in an app package, for the stylesheets of a shadow root.
 *
 * An app package links Kit H firmly and, when `designKit` in `config.js` chooses another kit, loads that kit's token
 * file after it ( `kitLader()` in `apps/build/package-parts.mjs` ). The loader leaves the address in
 * `window.einvoiceKitTokens`. A shadow root does not see the document's stylesheets, so every element that links its
 * own from `data-css` passes the list through kitStylesheets(), and the chosen token file joins the list right after
 * the default one.
 *
 * ⚠️ **ADDED BEHIND, NOT SWAPPED IN.** The default token file stays in the list. A chosen file that is missing, or an
 * own kit with a misspelt name, then leaves Kit H in place instead of a view without colours, surfaces and font sizes.
 *
 * ⚠️ **IN A WORDPRESS PLUGIN NOTHING HAPPENS.** There the variable is not set: the plugin already hands the chosen
 * kit's addresses to `data-css` ( `EInvoicePress_Design_Kit::stylesheets()` ), and the list stays as it is.
 *
 * Since 19.10.2026 ( Ralf: one switch, and the whole interface keeps to the kit ).
 */

/** A token file of the tool kit, under its own name: `spt-kit-<x>.tokens.css`, with or without a path or query. */
const TOKENS = /(^|\/)spt-kit-[a-z0-9-]+\.tokens\.css(\?.*)?$/;

/**
 * The list of stylesheet addresses, with the chosen kit behind the first token file.
 *
 * @param {string[]} urls the addresses from `data-css` or a built-in default list
 * @return {string[]}
 */
export function kitStylesheets( urls ) {

	return mitKit( urls ).map( eingebettet );

}

/** The list with the chosen kit's token file behind the first token file. */
function mitKit( urls ) {

	const chosen = typeof globalThis !== 'undefined' && typeof globalThis.einvoiceKitTokens === 'string'
		? globalThis.einvoiceKitTokens
		: '';

	if ( chosen === '' || urls.includes( chosen ) )
		return urls;

	const at = urls.findIndex( ( u ) => TOKENS.test( u ) );

	if ( at === -1 )
		return urls;

	return [ ...urls.slice( 0, at + 1 ), chosen, ...urls.slice( at + 1 ) ];

}

/**
 * The address under which a one-file app serves a stylesheet it carries inside.
 *
 * ⚠️ **A ONE-FILE APP HAS NO FOLDER `assets/css/`.** It keeps the start page's `data-css` unchanged and carries the
 * stylesheets as text; `globalThis.einvoiceStyleUrl` ( `apps/build/one-file.mjs` ) turns a name into a `blob:` address of
 * that text. Without the function, in a folder package or a plugin, every address stays as it is.
 *
 * ⚠️ **AFTER THE KIT IS INSERTED, NOT BEFORE.** The token file is found by its name `spt-kit-<x>.tokens.css`; a `blob:`
 * address no longer carries it.
 *
 * @param {string} url
 * @return {string}
 */
function eingebettet( url ) {

	return typeof globalThis !== 'undefined' && typeof globalThis.einvoiceStyleUrl === 'function'
		? globalThis.einvoiceStyleUrl( url )
		: url;

}
